/*
 * IMPORTANT: ID Requirements
 * - Every div element in this file MUST have an ID attribute
 * - IDs should be placed immediately after the opening div tag
 * - IDs should follow the 'note-*' naming pattern
 * - When adding new divs, ensure they have unique, descriptive IDs
 * - DO NOT remove existing IDs during modifications
 *
 * NoteEditor Component
 * Purpose: Provides a comprehensive form interface for creating and editing notes
 * Logic:
 * - Manages local note state with validation
 * - Handles note type selection and team assignment
 * - Supports note metadata toggles and direct task creation for saved notes
 * - Provides save/cancel operations with proper error handling
 * - Integrates with NOTE_FLAGS system for flag management
 * - Uses entityTeamId as readonly pre-selection when creating notes for specific entities
 * - When entityTeamId is available, shows only that team as selected and readonly
 * - If entityTeamId is not available, shows all teams for user selection
 * - Loads persisted tasks linked to the current note and supports completion actions
 * - Loads persisted tasks linked to the current note and supports completion actions
 *
 * Changes:
 * - Resolves new-note visibility from the server; saved notes retain their persisted audience mode
 * - Adds single-select level chips, pending/error save protection, and above-own-level explanations
 * - Preserves draft content, type, and flags when association props or policy requests change
 * - Adds an opt-in new-note dialog layout that places Type, flags, and audience controls in the
 *   details section while omitting the redundant status row
 * - Preserves the existing default layout for every other NoteEditor consumer
 * - Stacks note metadata controls on narrow screens while preserving the desktop two-column layout
 * - Keeps action buttons reachable and readable in the compact account notes workspace
 * - Moves linked-task panel actions onto the visible cancel status workflow instead of delete
 * - Enforces the shared 65,536-character Note limit with a live remaining-character helper in every layout
 * - Preserves legacy over-limit notes until the user deliberately reduces them before saving
 * - Keeps existing level-note creator post-save actions available when the selected minimum exceeds their access level
 * - Aligns the popup-only layout, labels, switches, audience panel, and footer with task dialogs
 * - Splits popup note content into joined title/body fields while preserving first-line storage
 * - Groups popup association and note fields so their compact spacing is independent of details
 * - Adds a shared-style workspace panel for account/contact note creation and editing
 * - Requires a substantive popup note body in addition to the title
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Note, NOTE_TYPES, NOTE_FLAGS } from '../../models/Note';
import { Task } from '../../models/Task';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { TASKS_UPDATED_EVENT, getTasksForNote, updateTaskStatus } from '../../services/taskService';
import TeamSelector from '../common/TeamSelector';
import NoteTaskListDialog from './NoteTaskListDialog';
import { NOTE_MAX_LENGTH } from '../../constants/noteConstants';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { isAccessLevel, type AccessLevel, type NoteVisibilityMode } from '../../constants/noteVisibilityConstants';
import { request } from '../../services/dataService';
import { useTeams } from '../../context/TeamContext';
import { Button } from '../primitives';
import NoteLevelSelector from './NoteLevelSelector';
import {
  composeJoinedText,
  JoinedTextFields,
  POPUP_HEADER_CLASS_NAME,
  POPUP_LABEL_CLASS_NAME,
  POPUP_TITLE_CLASS_NAME,
  PopupFooterSurface,
  splitJoinedText,
} from '../popup';

type NoteType = typeof NOTE_TYPES[number];
type NoteEditorLayout = 'default' | 'new-note-dialog' | 'workspace-panel';

interface NoteEditorProps {
  note: Note;
  accountName?: string;
  contactName?: string;
  onSave: (note: Note, context?: { canReadAfterSave: boolean }) => void;
  onCancel: () => void;
  onCreateTaskFromNote?: (note: Note) => void;
  currentUserId?: number;
  submitButtonText?: string;
  entityTeamId?: number;
  layout?: NoteEditorLayout;
  associationField?: React.ReactNode;
}

const NoteEditor: React.FC<NoteEditorProps> = ({
  note,
  accountName,
  contactName,
  onSave,
  onCancel,
  onCreateTaskFromNote,
  currentUserId,
  submitButtonText = note?.id ? 'Save Note' : 'Create Note',
  entityTeamId,
  layout = 'default',
  associationField,
}) => {
  // Local state for note data
  const [localNote, setLocalNote] = useState<Note>(note!);
  const { currentAccessLevel } = useTeams();
  const [policy, setPolicy] = useState<{
    key: string;
    visibilityMode: NoteVisibilityMode | null;
    accessLevel: AccessLevel | null;
    error: string | null;
  }>({ key: '', visibilityMode: null, accessLevel: null, error: null });
  const [policyAttempt, setPolicyAttempt] = useState(0);
  const policyKey = `${note.objectType}:${note.objectIds}`;
  const visibilityMode = note.id
    ? note.visibilityMode ?? 'teams'
    : policy.key === policyKey ? policy.visibilityMode : null;
  const policyError = !note.id && policy.key === policyKey ? policy.error : null;
  const effectiveAccessLevel = !note.id && policy.key === policyKey ? policy.accessLevel : currentAccessLevel;
  const isSavedNoteCreator = note.id > 0 && currentUserId != null && note.createdBy === currentUserId;
  const canReadAfterSave = !note.id || isSavedNoteCreator || visibilityMode === 'teams'
    || (isAccessLevel(effectiveAccessLevel) && isAccessLevel(localNote.minimumLevel)
      && effectiveAccessLevel >= localNote.minimumLevel);

  useEffect(() => {
    if (note.id) return;
    let canceled = false;
    setPolicy({ key: policyKey, visibilityMode: null, accessLevel: null, error: null });
    if (!note.objectIds.trim()) return;

    void request(API_ENDPOINTS.GET_NOTE_VISIBILITY_POLICY, {
      objectType: note.objectType,
      objectIds: note.objectIds,
    }).then(result => {
      if (canceled) return;
      if ((result?.visibilityMode !== 'teams' && result?.visibilityMode !== 'levels') || !isAccessLevel(result.accessLevel)) {
        throw new Error('Invalid note visibility policy');
      }
      setPolicy({ key: policyKey, visibilityMode: result.visibilityMode, accessLevel: result.accessLevel, error: null });
    }).catch(() => {
      if (!canceled) setPolicy({
        key: policyKey,
        visibilityMode: null,
        accessLevel: null,
        error: 'Unable to determine who can view this note. Try again.',
      });
    });

    return () => { canceled = true; };
  }, [note.id, note.objectType, note.objectIds, policyKey, policyAttempt]);

  // Sync localNote when note prop changes
  useEffect(() => {
    setLocalNote(previous => {
      if (note.id || previous.id) return note;
      const associationChanged = previous.objectType !== note.objectType || previous.objectIds !== note.objectIds;
      return {
        ...note,
        content: previous.content,
        type: previous.type,
        flags: previous.flags,
        teamIds: associationChanged ? note.teamIds : previous.teamIds,
        minimumLevel: associationChanged ? null : previous.minimumLevel,
      };
    });
  }, [note]);

  // Set entityTeamId when provided and teamIds is empty
  useEffect(() => {
    if (visibilityMode === 'teams' && entityTeamId && !localNote.teamIds) {
      setLocalNote(prev => ({ ...prev, teamIds: entityTeamId.toString() }));
    }
  }, [entityTeamId, localNote.teamIds, visibilityMode]);

  // Validation state
  const [errors, setErrors] = useState({
    content: '',
    teamIds: '',
    visibility: '',
  });
  const [linkedTasks, setLinkedTasks] = useState<Task[]>([]);
  const [isTaskListOpen, setIsTaskListOpen] = useState(false);

  useEffect(() => {
    const syncLinkedTasks = () => {
      if (!note?.id) {
        setLinkedTasks([]);
        return;
      }

      void getTasksForNote(note.id)
        .then((tasks) => setLinkedTasks(tasks))
        .catch((error) => {
          console.error('Failed to load note-linked tasks:', error);
          setLinkedTasks([]);
        });
    };

    syncLinkedTasks();

    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener(TASKS_UPDATED_EVENT, syncLinkedTasks);
    window.addEventListener('storage', syncLinkedTasks);

    return () => {
      window.removeEventListener(TASKS_UPDATED_EVENT, syncLinkedTasks);
      window.removeEventListener('storage', syncLinkedTasks);
    };
  }, [note?.id]);

  // Initialize note data when props change
  const handleContentChange = (content: string) => {
    const currentContent = localNote.content || '';
    const canAcceptChange = content.length <= NOTE_MAX_LENGTH
      || content.length < currentContent.length;

    if (!canAcceptChange) return;

    setLocalNote(prev => ({ ...prev, content }));
    setErrors(previous => ({
      ...previous,
      content: content.length > NOTE_MAX_LENGTH
        ? `Note must be ${NOTE_MAX_LENGTH.toLocaleString()} characters or fewer`
        : ''
    }));
  };

  const handleContentPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!isEditable) return;

    const currentContent = localNote.content || '';
    const textarea = event.currentTarget;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const pastedText = event.clipboardData.getData('text');
    const availableCharacters = NOTE_MAX_LENGTH - (currentContent.length - (selectionEnd - selectionStart));

    if (pastedText.length <= availableCharacters) return;

    event.preventDefault();

    if (currentContent.length > NOTE_MAX_LENGTH) {
      setErrors(previous => ({
        ...previous,
        content: `Note must be ${NOTE_MAX_LENGTH.toLocaleString()} characters or fewer`
      }));
      return;
    }

    const acceptedText = pastedText.slice(0, Math.max(availableCharacters, 0));
    const content = `${currentContent.slice(0, selectionStart)}${acceptedText}${currentContent.slice(selectionEnd)}`;
    setLocalNote(prev => ({ ...prev, content }));
    setErrors(previous => ({
      ...previous,
      content: `Note is limited to ${NOTE_MAX_LENGTH.toLocaleString()} characters. Extra text was removed.`
    }));
  };

  const handlePopupContentChange = (field: 'title' | 'body', value: string) => {
    const currentFields = splitJoinedText(localNote.content || '');
    handleContentChange(composeJoinedText(
      field === 'title' ? value : currentFields.title,
      field === 'body' ? value : currentFields.body,
    ));
  };

  const handlePopupContentPaste = (
    event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: 'title' | 'body',
  ) => {
    if (!isEditable) return;

    const currentContent = localNote.content || '';
    const currentFields = splitJoinedText(currentContent);
    const currentValue = currentFields[field];
    const selectionStart = event.currentTarget.selectionStart ?? currentValue.length;
    const selectionEnd = event.currentTarget.selectionEnd ?? selectionStart;
    const pastedText = event.clipboardData.getData('text');
    const candidateValue = `${currentValue.slice(0, selectionStart)}${pastedText}${currentValue.slice(selectionEnd)}`;
    const candidateContent = composeJoinedText(
      field === 'title' ? candidateValue : currentFields.title,
      field === 'body' ? candidateValue : currentFields.body,
    );

    if (candidateContent.length <= NOTE_MAX_LENGTH) return;

    event.preventDefault();

    if (currentContent.length > NOTE_MAX_LENGTH) {
      setErrors(previous => ({
        ...previous,
        content: `Note must be ${NOTE_MAX_LENGTH.toLocaleString()} characters or fewer`
      }));
      return;
    }

    const valueWithoutSelection = `${currentValue.slice(0, selectionStart)}${currentValue.slice(selectionEnd)}`;
    const contentWithoutPaste = composeJoinedText(
      field === 'title' ? valueWithoutSelection : currentFields.title,
      field === 'body' ? valueWithoutSelection : currentFields.body,
    );
    const oneCharacterContent = composeJoinedText(
      field === 'title' ? `${valueWithoutSelection}x` : currentFields.title,
      field === 'body' ? `${valueWithoutSelection}x` : currentFields.body,
    );
    const firstCharacterCost = oneCharacterContent.length - contentWithoutPaste.length;
    const availableCharacters = Math.max(NOTE_MAX_LENGTH - contentWithoutPaste.length - firstCharacterCost + 1, 0);
    const acceptedText = pastedText.slice(0, availableCharacters);
    const nextValue = `${currentValue.slice(0, selectionStart)}${acceptedText}${currentValue.slice(selectionEnd)}`;

    handleContentChange(composeJoinedText(
      field === 'title' ? nextValue : currentFields.title,
      field === 'body' ? nextValue : currentFields.body,
    ));
    setErrors(previous => ({
      ...previous,
      content: `Note is limited to ${NOTE_MAX_LENGTH.toLocaleString()} characters. Extra text was removed.`
    }));
  };

  const handleTypeChange = (type: NoteType) => {
    setLocalNote(prev => ({ ...prev, type }));
  };

  const handleNewsChange = (isChecked: boolean) => {
    setLocalNote(prev => {
      if (isChecked) {
        return NOTE_FLAGS.setFlag(prev as Note, NOTE_FLAGS.NEWS);
      } else {
        return NOTE_FLAGS.clearFlag(prev as Note, NOTE_FLAGS.NEWS);
      }
    });
  };

  const handleTasksChange = (isChecked: boolean) => {
    setLocalNote(prev => {
      if (isChecked) {
        return NOTE_FLAGS.setFlag(prev as Note, NOTE_FLAGS.TASKS);
      } else {
        return NOTE_FLAGS.clearFlag(prev as Note, NOTE_FLAGS.TASKS);
      }
    });
  };

  const handleTeamChange = (teamIds: string) => {
    setLocalNote(prev => ({ ...prev, teamIds }));
    if (teamIds) {
      setErrors(prev => ({ ...prev, teamIds: '' }));
    } else {
      setErrors(prev => ({ ...prev, teamIds: 'Please select at least one team' }));
    }
  };

  const handleLevelChange = (minimumLevel: AccessLevel) => {
    setLocalNote(previous => ({ ...previous, minimumLevel }));
    setErrors(previous => ({ ...previous, visibility: '' }));
  };

  const validateNote = (): boolean => {
    let valid = true;
    const newErrors = { content: '', teamIds: '', visibility: '' };

    if (usesJoinedFields) {
      const noteFields = splitJoinedText(localNote.content || '');
      if (!noteFields.title.trim()) {
        newErrors.content = 'Please enter a note title';
        valid = false;
      } else if (!noteFields.body.trim()) {
        newErrors.content = 'Please enter note body';
        valid = false;
      }
    } else if (!localNote.content?.trim()) {
      newErrors.content = 'Please enter note content';
      valid = false;
    }

    if (valid && localNote.content.length > NOTE_MAX_LENGTH) {
      newErrors.content = `Note must be ${NOTE_MAX_LENGTH.toLocaleString()} characters or fewer`;
      valid = false;
    }

    if (!visibilityMode) {
      newErrors.visibility = policyError || 'Select an account or contact and wait for visibility to load.';
      valid = false;
    } else if (visibilityMode === 'levels' && !isAccessLevel(localNote.minimumLevel)) {
      newErrors.visibility = 'Please select a level';
      valid = false;
    } else if (visibilityMode === 'teams' && !localNote.teamIds) {
      newErrors.teamIds = 'Please select at least one team';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditable) return;

    if (validateNote()) {
      // Create a new complete note object with calculated title
      const fullNote = {
        ...localNote,
        visibilityMode,
        minimumLevel: visibilityMode === 'levels' ? localNote.minimumLevel : null,
        teamIds: visibilityMode === 'levels' ? '' : localNote.teamIds,
        title: localNote.content?.split('\n')?.[0]?.slice(0, 250) ?? '',
      } as Note;

      onSave(fullNote, { canReadAfterSave });
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleOpenTaskList = () => {
    setIsTaskListOpen(true);
  };

  const handleCreateTaskFromNote = () => {
    if (!note?.id || !onCreateTaskFromNote) {
      return;
    }

    onCreateTaskFromNote({
      ...localNote,
      title: localNote.content?.split('\n')?.[0]?.slice(0, 250) ?? '',
    } as Note);
  };

  const handleCloseTaskList = () => {
    setIsTaskListOpen(false);
  };

  const handleCompleteTask = async (taskId: number) => {
    try {
      const updatedTask = await updateTaskStatus(taskId, 'Done');
      setLinkedTasks((previousTasks) => previousTasks.map((task) => task.id === taskId ? updatedTask : task));
      toast.success('Task marked as completed.');
    } catch (error) {
      console.error('Unable to update task status:', error);
      toast.error('Unable to update task status.');
    }
  };

  const handleCancelTask = async (taskId: number) => {
    if (!window.confirm('Cancel this task?')) {
      return;
    }

    try {
      const updatedTask = await updateTaskStatus(taskId, 'Canceled');
      setLinkedTasks((previousTasks) => previousTasks.map((task) => task.id === taskId ? updatedTask : task));
      toast.success('Task moved to Cancel.');
    } catch (error) {
      console.error('Unable to cancel task:', error);
      toast.error('Unable to cancel task.');
    }
  };

  const selectedTeamIds = (value?: string) => value !== undefined ? handleTeamChange(value) : (localNote.teamIds || '');

  if (!note) return null;

  const isEditable = note.createdBy === currentUserId;
  const shouldShowTaskFlagToggle = !note?.id;
  const shouldShowCreateTaskButton = Boolean(note?.id && onCreateTaskFromNote);
  const shouldShowTaskListButton = linkedTasks.length > 0;
  const isNewNoteDialogLayout = layout === 'new-note-dialog';
  const isWorkspacePanelLayout = layout === 'workspace-panel';
  const usesJoinedFields = isNewNoteDialogLayout || isWorkspacePanelLayout;
  const usesPopupControls = usesJoinedFields;
  const noteCharacterCount = localNote.content?.length || 0;
  const noteCharactersRemaining = Math.max(NOTE_MAX_LENGTH - noteCharacterCount, 0);
  const noteContentFields = splitJoinedText(localNote.content || '');
  const associationName = contactName || accountName;
  const joinedNoteFields = (
    <>
      <JoinedTextFields
        idPrefix="note"
        groupLabel="Note"
        titleLabel="Note title"
        bodyLabel="Note body"
        titlePlaceholder="Note title"
        bodyPlaceholder="Note body"
        title={noteContentFields.title}
        body={noteContentFields.body}
        onTitleChange={(value) => handlePopupContentChange('title', value)}
        onBodyChange={(value) => handlePopupContentChange('body', value)}
        onTitlePaste={(event) => handlePopupContentPaste(event, 'title')}
        onBodyPaste={(event) => handlePopupContentPaste(event, 'body')}
        readOnly={!isEditable}
        requiredTitle
        requiredBody
        invalid={Boolean(errors.content)}
        describedBy={`note-content-character-count${errors.content ? ' note-content-error' : ''}`}
        className="flex min-h-0 flex-1 flex-col"
        surfaceClassName="flex min-h-0 flex-1 flex-col"
        bodyClassName="min-h-[14rem] flex-1 resize-none"
        testId="note-joined-fields"
      />
      <p
        id="note-content-character-count"
        className={`shrink-0 pt-1 text-right text-xs ${THEME_CLASSES.text.secondary}`}
        aria-live="polite"
      >
        {noteCharactersRemaining.toLocaleString()} characters remaining
      </p>
    </>
  );

  return (
    <>
      <form
        id="note-editor-form"
        onSubmit={handleSubmit}
        noValidate={usesJoinedFields}
        className={isNewNoteDialogLayout ? 'contents' : 'flex flex-col h-full overflow-hidden'}
      >
      {/* Title */}
      {!isNewNoteDialogLayout && (
        <div id="note-header" className={`${isWorkspacePanelLayout ? POPUP_HEADER_CLASS_NAME : 'px-4 py-3 sm:px-6'} ${THEME_CLASSES.border} border-b`}>
          {isWorkspacePanelLayout ? (
            <h3 id="note-editor-heading" className={POPUP_TITLE_CLASS_NAME}>{note?.id ? 'Edit note' : 'New note'}</h3>
          ) : (
            <>
              {(accountName || contactName) && (
                <div id="note-title" className={`text-lg font-semibold ${THEME_CLASSES.text.primary} mb-2`}>
                  {contactName || accountName}
                  {contactName && accountName && (
                    <span id="note-subtitle" className={`text-sm ${THEME_CLASSES.text.secondary} ml-2`}>
                      ({accountName})
                    </span>
                  )}
                </div>
              )}
              <div id="note-status" className={`text-xs font-medium ${THEME_CLASSES.text.secondary}`}>
                {note?.id ? 'Editing Note' : 'Creating Note'}
              </div>
            </>
          )}
        </div>
      )}
      {/* Note Content */}
      {isNewNoteDialogLayout ? (
        <div id="note-primary-fields" className="flex min-h-0 min-w-0 flex-col gap-5 lg:col-start-1 lg:row-span-2 lg:row-start-1">
          {associationField && <div id="note-association-field" className="min-w-0">{associationField}</div>}
          <div id="note-content-section" className="flex min-h-[18rem] min-w-0 flex-1 flex-col">
            {joinedNoteFields}
          </div>
        </div>
      ) : isWorkspacePanelLayout ? (
        <div id="note-workspace-primary" className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
          {associationName && (
            <div id="note-workspace-association" className="min-w-0 shrink-0">
              <div id="note-workspace-association-label" className={POPUP_LABEL_CLASS_NAME}>Account or Contact</div>
              <div
                id="note-workspace-association-value"
                className={`inline-flex max-w-full items-center rounded-lg px-3 py-2 text-sm ${contactName
                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-200'
                  : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200'}`}
              >
                <span id="note-workspace-association-name" className="truncate">{associationName}</span>
              </div>
            </div>
          )}
          <div id="note-content-section" className="flex min-h-[18rem] min-w-0 flex-1 flex-col">
            {joinedNoteFields}
          </div>
        </div>
      ) : (
        <div id="note-content-section" className="flex-[0.7] flex flex-col overflow-auto p-4 sm:p-6">
          <label id="note-content-label" htmlFor="note-content" className={`text-xs font-medium ${THEME_CLASSES.text.secondary} mb-2 shrink-0`}>
            Note
          </label>
          <textarea
            id="note-content"
            value={localNote.content || ''}
            onChange={(e) => handleContentChange(e.target.value)}
            onPaste={handleContentPaste}
            readOnly={!isEditable}
            maxLength={noteCharacterCount > NOTE_MAX_LENGTH ? undefined : NOTE_MAX_LENGTH}
            aria-describedby={`note-content-character-count${errors.content ? ' note-content-error' : ''}`}
            className={`h-full min-h-[200px] w-full resize-none ${THEME_CLASSES.input.textarea} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.text} ${THEME_CLASSES.input.placeholder} ${THEME_CLASSES.input.base} ${!isEditable ? 'cursor-not-allowed opacity-75' : ''}`}
            placeholder="Enter note title and content"
          />
          <p
            id="note-content-character-count"
            className={`shrink-0 pt-1 text-right text-xs ${THEME_CLASSES.text.secondary}`}
            aria-live="polite"
          >
            {noteCharactersRemaining.toLocaleString()} characters remaining
          </p>
        </div>
      )}

      {/* Note Type and News and Teams */}
      <div
        id="note-options"
        className={isNewNoteDialogLayout
          ? 'contents'
          : isWorkspacePanelLayout
            ? `grid shrink-0 grid-cols-1 gap-5 border-t px-4 py-4 sm:grid-cols-2 sm:px-5 ${THEME_CLASSES.border}`
          : `flex-[0.3] min-h-[150px] grid grid-cols-2 gap-3 px-4 py-4 sm:px-6 sm:gap-4 ${THEME_CLASSES.border} border-t`
        }
      >
        <div
          id="note-type-selector"
          className={isNewNoteDialogLayout
            ? 'flex min-w-0 flex-col gap-4 lg:col-start-2 lg:row-start-1'
            : isWorkspacePanelLayout
              ? 'flex min-w-0 flex-col gap-4'
            : 'flex flex-col border-r pr-3 pb-0 sm:pr-4'
          }
        >
          {/* Note Type */}
          <div id="note-type-field" className={usesPopupControls ? 'w-full' : 'mb-4'}>
            <label id="note-type-label" htmlFor="note-type" className={usesPopupControls ? POPUP_LABEL_CLASS_NAME : `text-xs font-medium ${THEME_CLASSES.text.secondary} mb-2`}>
              Type
            </label>
            <select
              id="note-type"
              value={localNote.type as string}
              onChange={(e) => handleTypeChange(e.target.value as NoteType)}
              disabled={!isEditable}
              className={`block w-full ${usesPopupControls ? '!rounded-xl px-3 py-2 text-sm' : ''} ${THEME_CLASSES.input.field} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.text} ${THEME_CLASSES.input.base} ${!isEditable ? 'cursor-not-allowed opacity-75' : ''}`}
            >
              {NOTE_TYPES.filter(noteType => noteType !== 'All').map(noteType => (
                <option key={noteType} value={noteType}>{noteType}</option>
              ))}
            </select>
          </div>
          {/* News and task flag controls */}
          <div id="note-news-container" className={usesPopupControls ? 'flex shrink-0 flex-wrap items-center gap-5 rounded-xl bg-slate-100 p-3 dark:bg-slate-900' : 'mb-4 flex flex-wrap items-center gap-4'}>
            <label className={`inline-flex items-center cursor-pointer ${!isEditable ? 'cursor-not-allowed opacity-75' : ''}`}>
              <input
                type="checkbox"
                checked={NOTE_FLAGS.hasFlag(localNote as Note, NOTE_FLAGS.NEWS)}
                onChange={(e) => handleNewsChange(e.target.checked)}
                disabled={!isEditable}
                className={usesPopupControls ? 'peer sr-only' : `rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 ${!isEditable ? 'cursor-not-allowed' : ''}`}
              />
              {usesPopupControls && <span aria-hidden="true" className="relative h-5 w-9 shrink-0 rounded-full bg-slate-300 transition-colors after:absolute after:left-1 after:top-1 after:h-3 after:w-3 after:rounded-full after:bg-white after:shadow-sm after:transition-transform peer-checked:bg-blue-600 peer-checked:after:translate-x-4 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2 peer-disabled:opacity-60 dark:bg-slate-700" />}
              <span className={`${usesPopupControls ? 'ml-2 text-sm' : 'ml-2 text-xs'} font-medium ${THEME_CLASSES.text.secondary}`}>
                News
              </span>
            </label>
            {shouldShowTaskFlagToggle && (
              <label className={`inline-flex items-center cursor-pointer ${!isEditable ? 'cursor-not-allowed opacity-75' : ''}`}>
                <input
                  type="checkbox"
                  checked={NOTE_FLAGS.hasFlag(localNote as Note, NOTE_FLAGS.TASKS)}
                  onChange={(e) => handleTasksChange(e.target.checked)}
                  disabled={!isEditable}
                  className={usesPopupControls ? 'peer sr-only' : `rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 ${!isEditable ? 'cursor-not-allowed' : ''}`}
                />
                {usesPopupControls && <span aria-hidden="true" className="relative h-5 w-9 shrink-0 rounded-full bg-slate-300 transition-colors after:absolute after:left-1 after:top-1 after:h-3 after:w-3 after:rounded-full after:bg-white after:shadow-sm after:transition-transform peer-checked:bg-blue-600 peer-checked:after:translate-x-4 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2 peer-disabled:opacity-60 dark:bg-slate-700" />}
                <span className={`${usesPopupControls ? 'ml-2 text-sm' : 'ml-2 text-xs'} font-medium ${THEME_CLASSES.text.secondary}`}>
                  Tasks
                </span>
              </label>
            )}
          </div>
        </div>
        {/* Teams */}
        <div
          id="note-teams-selector"
          className={isNewNoteDialogLayout
            ? 'flex min-w-0 flex-col lg:col-start-2 lg:row-start-2'
            : isWorkspacePanelLayout
              ? 'flex min-w-0 flex-col'
            : 'flex flex-col pl-3 sm:pl-4'
          }
        >
          <div id="note-visibility-controls" className={usesPopupControls ? '' : 'mb-4'}>
            <label id="note-teams-label" className={usesPopupControls ? POPUP_LABEL_CLASS_NAME : `text-xs font-medium ${THEME_CLASSES.text.secondary} mb-2 block`}>
              {visibilityMode === 'teams' ? 'Teams' : 'Who can view'}
            </label>
            {visibilityMode === 'teams' ? <TeamSelector
              selectedTeamIds={selectedTeamIds}
              isEditable={isEditable && !entityTeamId}
              useAllTeamsForSelection={true}
              layout={usesPopupControls ? 'vertical' : undefined}
            /> : visibilityMode === 'levels' ? <NoteLevelSelector
              value={localNote.minimumLevel}
              onChange={handleLevelChange}
              isEditable={isEditable}
              layout={usesPopupControls ? 'segmented' : 'wrap'}
            /> : policyError ? (
              <div id="note-policy-error" className="space-y-2">
                <p role="alert" className="text-xs text-red-500">{policyError}</p>
                <Button type="button" variant="secondary" onClick={() => setPolicyAttempt(previous => previous + 1)}>Try again</Button>
              </div>
            ) : <p className={`text-xs ${THEME_CLASSES.text.secondary}`} role="status">
              {note.objectIds ? 'Loading visibility...' : 'Select an account or contact to set visibility.'}
            </p>}
            {isEditable && visibilityMode === 'levels' && isAccessLevel(localNote.minimumLevel) && !canReadAfterSave && (
              <p id="note-access-loss-help" className={`mt-3 text-xs ${THEME_CLASSES.text.secondary}`} role="status">
                This level is above your access. After saving, you will not be able to view this note or create tasks from it.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div
        id="note-actions"
        className={isNewNoteDialogLayout ? '-mx-5 shrink-0 sm:-mx-6 lg:col-span-2 lg:row-start-3' : isWorkspacePanelLayout ? 'shrink-0' : `${THEME_CLASSES.border} border-t shrink-0`}
      >
        {(errors.content || errors.teamIds || errors.visibility) && (
          <div id="note-errors" className="px-4 pt-4 sm:px-6">
            {errors.content && (
              <div id="note-content-error" className="text-xs text-red-500" role="alert">{errors.content}</div>
            )}
            {errors.teamIds && (
              <div id="note-team-error" className="text-xs text-red-500">{errors.teamIds}</div>
            )}
            {errors.visibility && <div id="note-visibility-error" className="text-xs text-red-500" role="alert">{errors.visibility}</div>}
          </div>
        )}
        {usesPopupControls ? (
          <PopupFooterSurface
            testId={isWorkspacePanelLayout ? 'note-workspace-footer' : 'note-popup-footer'}
            actions={(
              <div id="note-button-group" className="flex w-full flex-wrap items-center justify-end gap-3">
                {shouldShowTaskListButton && (
                  <button
                    id="note-view-task-list"
                    type="button"
                    onClick={handleOpenTaskList}
                    className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg px-4 py-2 text-xs font-medium ${THEME_CLASSES.button.secondary}`}
                  >
                    View Task List
                  </button>
                )}
                <Button id="note-cancel" type="button" onClick={handleCancel} variant="secondary" size="sm">Cancel</Button>
                {shouldShowCreateTaskButton && (
                  <button
                    id="note-create-task"
                    type="button"
                    onClick={handleCreateTaskFromNote}
                    className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-xs font-medium text-emerald-800 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-900/60"
                  >
                    Create Task from Note
                  </button>
                )}
                <Button id="note-submit" type="submit" size="sm" disabled={!isEditable || !visibilityMode}>{submitButtonText}</Button>
              </div>
            )}
          />
        ) : (
        <div id="note-actions-container" className="flex items-center justify-end gap-4 p-4 sm:px-6">
          <div id="note-button-group" className="ml-auto flex w-full items-center justify-end gap-3 sm:w-auto sm:gap-4">
            {shouldShowTaskListButton && (
              <button
                id="note-view-task-list"
                type="button"
                onClick={handleOpenTaskList}
                className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap px-4 py-2 rounded-md text-xs font-medium ${THEME_CLASSES.button.secondary}`}
              >
                View Task List
              </button>
            )}
            <button
              id="note-cancel"
              type="button"
              onClick={handleCancel}
              className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap px-4 py-2 rounded-md text-xs font-medium ${THEME_CLASSES.button.secondary}`}
            >
              Cancel
            </button>
            {shouldShowCreateTaskButton && (
              <button
                id="note-create-task"
                type="button"
                onClick={handleCreateTaskFromNote}
                className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-xs font-medium text-emerald-800 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-900/60"
              >
                Create Task from Note
              </button>
            )}
            <button
              id="note-submit"
              type="submit"
              disabled={!isEditable || !visibilityMode}
              className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap px-4 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium ${isEditable && visibilityMode
                ? THEME_CLASSES.button.primary
                : `${THEME_CLASSES.button.primary} opacity-50 cursor-not-allowed`
                }`}
            >
              {submitButtonText}
            </button>
          </div>
        </div>
        )}
      </div>
      {!usesPopupControls && <div id="note-spacer" className="shrink-0">&nbsp;</div>}
      </form>
      <NoteTaskListDialog
        open={isTaskListOpen}
        noteId={note.id}
        tasks={linkedTasks}
        currentUserId={currentUserId ?? null}
        onClose={handleCloseTaskList}
        onCompleteTask={handleCompleteTask}
        onCancelTask={handleCancelTask}
      />
    </>
  );
};

export default NoteEditor;
